import React, { useEffect, useState } from 'react';
import SignInRequiredModal from '../SignInRequiredModal';

const UploadPanel = ({
  user,
  isUserSignedIn,
  handleLogout,
  setIsUserSignedIn,
  setUser,
  setDoctors,
  setInsurances,
  setBodilyParts,
  setOriginalDoctors,
  selectedFile,
  isUploading,
  handleFileChange,
  handleClearSelectedFile,
  fileInputResetKey,
  handleUploadAndGenerateSubtitles,
  videoValidationError,
  selectedVideoDuration,
  maxBetaVideoDurationSeconds,
  formatVideoDuration,
}) => {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [processingSecondsRemaining, setProcessingSecondsRemaining] = useState(300);

  const isSignedIn = Boolean(user && isUserSignedIn !== false);
  const isAdminUser = Boolean(
    isSignedIn &&
      user &&
      (user.admin === true || user.super_admin === true)
  );

  const effectiveRightsConfirmed = Boolean(isAdminUser || rightsConfirmed);

  useEffect(() => {
    if (isAdminUser) {
      setRightsConfirmed(true);
      return;
    }

    if (!isSignedIn) {
      setRightsConfirmed(false);
    }
  }, [isAdminUser, isSignedIn]);

  useEffect(() => {
    let countdownInterval = null;

    if (isUploading) {
      setProcessingSecondsRemaining(300);

      countdownInterval = window.setInterval(() => {
        setProcessingSecondsRemaining((previousSeconds) => {
          if (previousSeconds <= 0) {
            return 0;
          }

          return previousSeconds - 1;
        });
      }, 1000);
    } else {
      setProcessingSecondsRemaining(300);
    }

    return () => {
      if (countdownInterval) {
        window.clearInterval(countdownInterval);
      }
    };
  }, [isUploading]);

  const formatProcessingCountdown = (secondsRemaining) => {
    if (!isUploading) {
      return 'Processing — could take up to 5 min';
    }

    if (secondsRemaining <= 0) {
      return 'Still working...';
    }

    if (secondsRemaining <= 30) {
      return 'Finalizing...';
    }

    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    const paddedSeconds = String(seconds).padStart(2, '0');

    return `Processing — could be ${minutes}:${paddedSeconds} longer`;
  };

  const shouldAnimateChooseFile = Boolean(isSignedIn && !selectedFile && !isUploading);
  const shouldAnimateUploadButton = Boolean(
    isSignedIn &&
      selectedFile &&
      effectiveRightsConfirmed &&
      !videoValidationError &&
      !isUploading
  );

  const uploadButtonDisabled =
    isUploading ||
    (isSignedIn &&
      (!selectedFile || Boolean(videoValidationError) || !effectiveRightsConfirmed));

  const selectedDurationText =
    selectedVideoDuration !== null &&
    selectedVideoDuration !== undefined &&
    typeof formatVideoDuration === 'function'
      ? formatVideoDuration(selectedVideoDuration)
      : null;

  const maxDurationText =
    maxBetaVideoDurationSeconds !== null &&
    maxBetaVideoDurationSeconds !== undefined &&
    typeof formatVideoDuration === 'function'
      ? formatVideoDuration(maxBetaVideoDurationSeconds)
      : '1:00';

  const processingCountdownText = formatProcessingCountdown(processingSecondsRemaining);

  const openSignInModal = () => {
    setShowSignInModal(true);
  };

  const closeSignInModal = () => {
    setShowSignInModal(false);
  };

  const handleLockedUploadClick = (event) => {
    if (!isSignedIn) {
      event.preventDefault();
      event.stopPropagation();
      openSignInModal();
    }
  };

  const handleClearClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (typeof handleClearSelectedFile === 'function') {
      handleClearSelectedFile();
    }

    setRightsConfirmed(isAdminUser);
  };

  const handleRightsConfirmedChange = (event) => {
    if (isAdminUser) {
      setRightsConfirmed(true);
      return;
    }

    setRightsConfirmed(event.target.checked);
  };

  const handleUploadClick = (event) => {
    if (!isSignedIn) {
      event.preventDefault();
      openSignInModal();
      return;
    }

    if (!selectedFile || Boolean(videoValidationError) || !effectiveRightsConfirmed) {
      event.preventDefault();
      return;
    }

    if (typeof handleUploadAndGenerateSubtitles === 'function') {
      handleUploadAndGenerateSubtitles({
        rightsConfirmed: effectiveRightsConfirmed,
      });
    }
  };

  return (
    <>
      <div className="relative mb-6 overflow-hidden rounded-3xl border border-white/10 bg-slate-950/60 p-4 shadow-2xl shadow-slate-950/40 backdrop-blur-xl">
        <div className="pointer-events-none absolute right-3 top-1/2 hidden h-32 w-64 -translate-y-1/2 rounded-full bg-amber-300/10 blur-3xl md:block" />
        <div className="pointer-events-none absolute -left-20 -top-20 h-52 w-52 rounded-full bg-cyan-300/10 blur-3xl" />

        <div className="relative mb-4 grid gap-3 rounded-2xl border border-cyan-300/15 bg-cyan-400/[0.04] px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">
              60 Second Beta
            </p>

            <p className="mt-1 text-sm leading-6 text-slate-300">
              Each upload supports videos up to{' '}
              <span className="font-black text-white">{maxDurationText}</span>.
              Downloads are temporary and may expire shortly after processing.
            </p>
          </div>

          <div className="w-fit rounded-full border border-emerald-300/25 bg-emerald-400/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-emerald-200 shadow-lg shadow-emerald-950/20">
            Beta Ready
          </div>
        </div>

        {!isSignedIn && (
          <div className="relative z-20 mb-4 overflow-hidden rounded-2xl border border-amber-300/20 bg-amber-400/10 px-4 py-4 shadow-lg shadow-amber-950/20">
            <div className="pointer-events-none absolute -right-12 -top-12 h-28 w-28 rounded-full bg-amber-300/20 blur-2xl" />

            <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-amber-200">
                  Sign In Required
                </p>

                <p className="mt-1 text-sm leading-6 text-amber-50/80">
                  Sign in to upload a video and start subtitle processing.
                </p>
              </div>

              <button
                type="button"
                onClick={openSignInModal}
                className="inline-flex min-h-[44px] w-full items-center justify-center rounded-2xl border border-amber-200/40 bg-amber-300/15 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-amber-50 shadow-lg shadow-amber-950/20 transition hover:-translate-y-0.5 hover:border-cyan-200/70 hover:bg-cyan-300/15 sm:w-auto"
              >
                Sign In
              </button>
            </div>
          </div>
        )}

        <div className="relative flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <label
            onClick={handleLockedUploadClick}
            className={[
              'group relative z-30 flex w-full flex-col rounded-2xl border border-dashed px-4 py-4 text-sm shadow-lg shadow-slate-950/20 transition md:flex-1',
              isSignedIn
                ? 'cursor-pointer border-emerald-300/30 bg-slate-950/80 text-slate-300 hover:border-emerald-300/70 hover:bg-emerald-400/10'
                : 'cursor-pointer border-amber-300/20 bg-slate-950/60 text-slate-400 hover:border-amber-200/50 hover:bg-amber-400/10',
            ].join(' ')}
          >
            <span
              className={[
                'relative z-40 text-xs font-black uppercase tracking-[0.22em]',
                isSignedIn ? 'text-emerald-300' : 'text-amber-200',
              ].join(' ')}
            >
              Select Video
            </span>

            <div className="relative z-40 mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <span
                className={[
                  'relative inline-flex min-h-[42px] w-fit max-w-full items-center justify-center overflow-hidden rounded-xl border px-4 py-2 text-xs font-black uppercase tracking-[0.12em] shadow-lg transition group-hover:-translate-y-0.5',
                  shouldAnimateChooseFile
                    ? 'border-emerald-300/65 bg-emerald-400/20 text-emerald-50 shadow-emerald-950/40 group-hover:border-lime-200/90 group-hover:bg-lime-300/18 group-hover:shadow-emerald-400/30'
                    : isSignedIn
                      ? 'border-emerald-300/25 bg-emerald-400/8 text-emerald-100/90 shadow-slate-950/20 group-hover:border-emerald-300/45 group-hover:bg-emerald-400/12'
                      : 'border-amber-300/25 bg-amber-400/10 text-amber-100 shadow-amber-950/20 group-hover:border-amber-200/60 group-hover:bg-amber-300/15',
                ].join(' ')}
              >
                <span className="relative z-20 opacity-100">
                  {isSignedIn ? 'Choose File' : 'Sign In To Choose'}
                </span>
              </span>

              <span className="relative z-30 flex min-w-0 max-w-full items-center gap-2 rounded-xl border border-white/10 bg-slate-950/60 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-slate-950/20">
                <span className="min-w-0 truncate">
                  {selectedFile ? selectedFile.name : 'No video selected'}
                </span>

                {selectedFile && (
                  <button
                    type="button"
                    onClick={handleClearClick}
                    disabled={isUploading}
                    aria-label="Clear selected video"
                    title="Clear selected video"
                    className="relative z-40 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-rose-300/30 bg-rose-400/10 text-sm font-black leading-none text-rose-100 shadow-lg shadow-rose-950/20 transition hover:-translate-y-0.5 hover:border-rose-200/80 hover:bg-rose-400/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
                  >
                    ×
                  </button>
                )}
              </span>
            </div>

            {selectedDurationText && (
              <p className="relative z-40 mt-3 text-xs font-bold text-cyan-100/80">
                Selected video length: {selectedDurationText}
              </p>
            )}

            <input
              key={fileInputResetKey}
              type="file"
              accept="video/*"
              disabled={!isSignedIn || isUploading}
              onChange={handleFileChange}
              className="sr-only"
            />
          </label>

          <div className="relative z-10 flex w-full flex-col md:w-auto md:min-w-[260px]">
            <div className="mb-4 rounded-2xl border border-cyan-300/20 bg-slate-900/70 p-4 text-left shadow-lg shadow-slate-950/20">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={effectiveRightsConfirmed}
                  onChange={handleRightsConfirmedChange}
                  disabled={!isSignedIn || isUploading}
                  className="mt-1 h-4 w-4 rounded border-cyan-300/40 bg-slate-950 text-cyan-400 focus:ring-cyan-400 disabled:cursor-not-allowed disabled:opacity-50"
                />

                <span className="text-sm leading-6 text-slate-200">
                  I confirm that I own this video or have permission to upload,
                  subtitle, process, and download it.
                </span>
              </label>

              {isAdminUser && (
                <p className="mt-3 rounded-xl border border-emerald-300/20 bg-emerald-400/10 px-3 py-2 text-xs font-bold leading-5 text-emerald-100">
                  Admin account detected. Rights confirmation is automatically checked.
                </p>
              )}

              <p className="mt-3 text-xs leading-5 text-slate-400">
                SubtitleTsunami is for original, licensed, or authorized content.
                Do not upload copyrighted videos you do not have permission to use.
              </p>
            </div>

            <button
              type="button"
              onClick={handleUploadClick}
              disabled={uploadButtonDisabled}
              className="group relative z-20 inline-flex min-h-[56px] w-full items-center justify-center overflow-hidden rounded-2xl border border-cyan-300/30 bg-cyan-400/15 px-4 py-3 text-xs font-black uppercase tracking-[0.1em] text-cyan-100 shadow-lg shadow-cyan-950/30 transition hover:-translate-y-0.5 hover:border-amber-200/80 hover:bg-amber-300/15 hover:shadow-amber-400/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 sm:text-sm md:w-auto"
            >
              {isUploading ? (
                <span className="relative z-20 inline-flex items-center justify-center gap-2 opacity-100">
                  <span className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-cyan-100/30 border-t-cyan-100" />
                  <span className="leading-tight">
                    {processingCountdownText}
                  </span>
                </span>
              ) : (
                <span className="relative z-20 opacity-100">
                  {isSignedIn ? 'Upload and Generate' : 'Sign In To Upload'}
                </span>
              )}
            </button>

            {shouldAnimateUploadButton && (
              <div className="pointer-events-none absolute bottom-0 left-0 right-0 -z-10 h-14 rounded-2xl bg-cyan-300/20 blur-xl" />
            )}
          </div>
        </div>

        {videoValidationError && (
          <div className="relative z-40 mt-4 rounded-2xl border border-rose-300/25 bg-rose-400/10 px-4 py-3">
            <p className="text-sm font-black text-rose-100">
              Video cannot be uploaded yet.
            </p>

            <p className="mt-1 text-xs leading-5 text-rose-100/75">
              {videoValidationError}
            </p>
          </div>
        )}
      </div>

      <SignInRequiredModal
        isOpen={showSignInModal}
        onClose={closeSignInModal}
        isUserSignedIn={isUserSignedIn}
        handleLogout={handleLogout}
        user={user}
        setIsUserSignedIn={setIsUserSignedIn}
        setUser={setUser}
        setDoctors={setDoctors}
        setInsurances={setInsurances}
        setBodilyParts={setBodilyParts}
        setOriginalDoctors={setOriginalDoctors}
      />
    </>
  );
};

export default UploadPanel;